import { useState, useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'react-toastify';
import { formularioSchema, type FormularioFormData } from '../schemas/formulario.schema';
import { criarFormulario, buscarFormulario, atualizarFormulario, enviarFormulario } from '../api/formulario';

export function useFormulario(existingId?: number) {
  const [formularioId, setFormularioId] = useState<number | null>(existingId ?? null);
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<FormularioFormData>({
    resolver: zodResolver(formularioSchema),
    defaultValues: {
      tipo_solicitacao: 'VendaConcluida',
      ampliacao_usina: false,
      tipo_localizacao: 'urbana',
      contrato_com_seguro: false,
      tipo_pessoa: 'PF',
      inversores: [{ quantidade: 1, potencia_kw: 0, ordem: 1 }],
      modo_pagamento: 'avista',
      proposta_crm: false,
      nome_verificador: 'Verificador de Contratos',
      email_verificador: 'validacao.contratos@multiluzsolar.com.br',
    },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (existingId) {
      buscarFormulario(existingId).then((data) => {
        // Map DB data back to form shape
        const formData: Partial<FormularioFormData> = {
          tipo_solicitacao: data.tipo_solicitacao,
          ampliacao_usina: data.ampliacao_usina,
          potencia_existente_kw: data.potencia_existente_kw ?? undefined,
          descricao_usina_existente: data.descricao_usina_existente ?? undefined,
          potencia_adquirida_kwp: data.potencia_adquirida_kwp,
          tipo_estrutura: data.tipo_estrutura,
          modalidade_compensacao: data.modalidade_compensacao,
          tipo_compensacao: data.tipo_compensacao ?? undefined,
          concessionaria_id: data.concessionaria_id,
          tipo_localizacao: data.tipo_localizacao,
          coordenadas_transformador: data.coordenadas_transformador ?? undefined,
          url_local_instalacao: data.url_local_instalacao ?? undefined,
          coordenada_geografica: data.coordenada_geografica ?? undefined,
          tipo_padrao: data.tipo_padrao ?? undefined,
          todas_contas_mesmo_titular: data.todas_contas_mesmo_titular ?? undefined,
          corrente_padrao: data.corrente_padrao ?? undefined,
          fases_disjuntor: data.fases_disjuntor ?? undefined,
          descricao_padrao: data.descricao_padrao ?? undefined,
          contrato_com_seguro: data.contrato_com_seguro,
          observacoes_finais: data.observacoes_finais ?? undefined,
          data_contrato: data.data_contrato ?? undefined,
          nome_verificador: data.nome_verificador ?? 'Verificador de Contratos',
          email_verificador: data.email_verificador ?? 'validacao.contratos@multiluzsolar.com.br',
        };

        if (data.cliente) {
          formData.tipo_pessoa = data.cliente.tipo_pessoa;
          formData.nome_completo = data.cliente.nome_completo;
          formData.email_cliente = data.cliente.email;
          formData.telefone = data.cliente.telefone;
          formData.cpf = data.cliente.cpf ?? undefined;
          formData.estado_civil = data.cliente.estado_civil ?? undefined;
          formData.profissao = data.cliente.profissao ?? undefined;
          formData.razao_social = data.cliente.razao_social ?? undefined;
          formData.cnpj = data.cliente.cnpj ?? undefined;
          formData.finalidade_empresa = data.cliente.finalidade_empresa ?? undefined;
          formData.cargo_representante = data.cliente.cargo_representante ?? undefined;

          if (data.cliente.endereco_faturamento) {
            const e = data.cliente.endereco_faturamento;
            formData.endereco_faturamento = { cep: e.cep, logradouro: e.logradouro, numero: e.numero, bairro: e.bairro, cidade: e.cidade, uf: e.uf, complemento: e.complemento ?? undefined, latitude: e.latitude ?? undefined, longitude: e.longitude ?? undefined };
          }
        }

        if (data.endereco_instalacao) {
          const e = data.endereco_instalacao;
          formData.endereco_instalacao = { cep: e.cep, logradouro: e.logradouro, numero: e.numero, bairro: e.bairro, cidade: e.cidade, uf: e.uf, complemento: e.complemento ?? undefined, latitude: e.latitude ?? undefined, longitude: e.longitude ?? undefined };
        }
        if (data.endereco_instalacao2) {
          const e = data.endereco_instalacao2;
          formData.endereco_instalacao2 = { cep: e.cep, logradouro: e.logradouro, numero: e.numero, bairro: e.bairro, cidade: e.cidade, uf: e.uf, complemento: e.complemento ?? undefined, latitude: e.latitude ?? undefined, longitude: e.longitude ?? undefined };
        }

        if (data.pagamento) {
          formData.valor_proposta = data.pagamento.valor_proposta;
          formData.origem_lead = data.pagamento.origem_lead ?? undefined;
          formData.pre_vendedor = data.pagamento.pre_vendedor ?? undefined;
          formData.proposta_crm = data.pagamento.proposta_crm;
          formData.numero_proposta = data.pagamento.numero_proposta ?? undefined;
          formData.modo_pagamento = data.pagamento.modo_pagamento;
          formData.descricao_pagamento = data.pagamento.descricao_pagamento ?? undefined;
          formData.payload_financeiro = data.pagamento.payload_financeiro ?? undefined;
        }

        if (data.modulos?.length) {
          formData.modulos = data.modulos;
        }
        if (data.inversores?.length) {
          formData.inversores = data.inversores;
        }

        form.reset(formData as FormularioFormData);
        setFormularioId(existingId);
      }).catch(() => {
        toast.error('Erro ao carregar formulário');
      });
    }
  }, [existingId]);

  const buildPayload = useCallback((data: FormularioFormData) => {
    const isVenda = data.tipo_solicitacao === 'VendaConcluida';

    const payload: Record<string, unknown> = {
      tipo_solicitacao: data.tipo_solicitacao,
      ampliacao_usina: data.ampliacao_usina,
      potencia_existente_kw: data.ampliacao_usina ? data.potencia_existente_kw : null,
      descricao_usina_existente: data.ampliacao_usina ? data.descricao_usina_existente : null,
      potencia_adquirida_kwp: data.potencia_adquirida_kwp,
      tipo_estrutura: data.tipo_estrutura,
      modalidade_compensacao: data.modalidade_compensacao,
      tipo_compensacao: data.modalidade_compensacao !== 'Autoconsumo Local' ? data.tipo_compensacao : null,
      concessionaria_id: data.concessionaria_id,
      tipo_localizacao: data.tipo_localizacao,
      coordenadas_transformador: data.tipo_localizacao === 'rural' ? data.coordenadas_transformador : null,
      endereco_instalacao: data.endereco_instalacao,
      endereco_instalacao2: data.endereco_instalacao2 || null,
      url_local_instalacao: data.url_local_instalacao,
      coordenada_geografica: data.coordenada_geografica,
      tipo_padrao: data.tipo_padrao,
      todas_contas_mesmo_titular: data.todas_contas_mesmo_titular,
      corrente_padrao: data.corrente_padrao,
      fases_disjuntor: data.fases_disjuntor,
      descricao_padrao: data.descricao_padrao,
      contrato_com_seguro: data.contrato_com_seguro,
      observacoes_finais: data.observacoes_finais,
      data_contrato: data.data_contrato || new Date().toISOString().split('T')[0],
      nome_verificador: data.nome_verificador,
      email_verificador: data.email_verificador,
      modulos: data.modulos,
      inversores: data.inversores,
      pagamento: isVenda ? {
        valor_proposta: data.valor_proposta,
        origem_lead: data.origem_lead,
        pre_vendedor: data.pre_vendedor,
        proposta_crm: data.proposta_crm,
        numero_proposta: data.numero_proposta,
        modo_pagamento: data.modo_pagamento,
        descricao_pagamento: data.descricao_pagamento,
        payload_financeiro: data.payload_financeiro,
      } : undefined,
    };

    if (isVenda) {
      payload.cliente = {
        tipo_pessoa: data.tipo_pessoa,
        nome_completo: data.nome_completo,
        email: data.email_cliente,
        telefone: data.telefone,
        cpf: data.cpf,
        estado_civil: data.estado_civil,
        profissao: data.profissao,
        razao_social: data.razao_social,
        cnpj: data.cnpj,
        finalidade_empresa: data.finalidade_empresa,
        cargo_representante: data.cargo_representante,
        endereco_faturamento: data.endereco_faturamento,
      };
    }

    return payload;
  }, []);

  const salvar = useCallback(async (data: FormularioFormData) => {
    setSubmitting(true);
    try {
      const payload = buildPayload(data);
      if (formularioId) {
        await atualizarFormulario(formularioId, payload);
        toast.success('Formulário atualizado');
      } else {
        const { id } = await criarFormulario(payload);
        setFormularioId(id);
        toast.success('Formulário criado');
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  }, [formularioId, buildPayload]);

  const enviar = useCallback(async (data: FormularioFormData) => {
    setSubmitting(true);
    try {
      const payload = buildPayload(data);
      let id = formularioId;
      if (!id) {
        const result = await criarFormulario(payload);
        id = result.id;
        setFormularioId(id);
      } else {
        await atualizarFormulario(id, payload);
      }
      await enviarFormulario(id);
      toast.success('Formulário enviado com sucesso!');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || 'Erro ao enviar');
    } finally {
      setSubmitting(false);
    }
  }, [formularioId, buildPayload]);

  return {
    form,
    formularioId,
    currentStep,
    setCurrentStep,
    submitting,
    salvar,
    enviar,
  };
}
